import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AdminRoute } from "./components/auth/AdminRoute";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { SessionAcrossTabs } from "./components/auth/SessionAcrossTabs";
import { AppShell } from "./components/layout/AppShell";
import { ApiDocs } from "./pages/ApiDocs";
import { Audit } from "./pages/Audit";
import { Dictionary } from "./pages/Dictionary";
import { ChangePassword } from "./pages/ChangePassword";
import { FileManager } from "./pages/FileManager";
import { Insights } from "./pages/Insights";
import { Login } from "./pages/Login";
import { Queue } from "./pages/Queue";
import { ProTrials } from "./pages/ProTrials";
import { Users } from "./pages/Users";
import { api } from "./lib/api";
import { loadRuntimeConfig } from "./lib/runtimeConfig";
import { Settings } from "./pages/Settings";
import { Usage } from "./pages/Usage";
import "./styles.css";

const ViewerPage = lazy(async () => {
  const module = await import("./pages/Viewer");
  return { default: module.Viewer };
});

const ComparePage = lazy(async () => {
  const module = await import("./pages/Compare");
  return { default: module.Compare };
});

function ViewerLoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center bg-ink-900 text-sm text-ink-300">
      Carregando visualizador...
    </div>
  );
}

// A config de runtime (GET /api/config) substitui as VITE_* de comportamento
// que antes ficavam congeladas no build. Falha é silenciosa: o app abre com os
// defaults em vez de travar (Fase 5 C3).
void loadRuntimeConfig(api.base);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/* Acima do Router de propósito (T-35): as rotas públicas ficam fora do
        ProtectedRoute, e o logout precisa alcançá-las também. */}
    <SessionAcrossTabs />
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Viewer renders without the App chrome → embed-friendly */}
        <Route
          path="/v/:slug"
          element={
            <Suspense fallback={<ViewerLoadingScreen />}>
              <ViewerPage />
            </Suspense>
          }
        />
        <Route
          path="/public-view/:viewToken"
          element={
            <Suspense fallback={<ViewerLoadingScreen />}>
              <ViewerPage />
            </Suspense>
          }
        />
        <Route
          path="/compare/:slugA/:slugB"
          element={
            <Suspense fallback={<ViewerLoadingScreen />}>
              <ComparePage />
            </Suspense>
          }
        />
        {/* A view pública de um PAR: o token é a credencial, e não há slug na
            URL — o mesmo motivo de /public-view/{token} (T-38). */}
        <Route
          path="/public-compare/:viewToken"
          element={
            <Suspense fallback={<ViewerLoadingScreen />}>
              <ComparePage />
            </Suspense>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/manager" replace />} />
          <Route path="/alterar-senha" element={<ChangePassword />} />
          <Route path="/manager" element={<FileManager />} />
          <Route path="/f/*" element={<FileManager />} />
          <Route
            path="/queue"
            element={
              <AdminRoute>
                <Queue />
              </AdminRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <AdminRoute>
                <Insights />
              </AdminRoute>
            }
          />
          <Route
            path="/uso"
            element={
              <AdminRoute>
                <Usage />
              </AdminRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <AdminRoute>
                <Audit />
              </AdminRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminRoute>
                <Users />
              </AdminRoute>
            }
          />
          <Route
            path="/testes-pro"
            element={
              <AdminRoute>
                <ProTrials />
              </AdminRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <AdminRoute>
                <Settings />
              </AdminRoute>
            }
          />
          <Route
            path="/dicionario"
            element={
              <AdminRoute>
                <Dictionary />
              </AdminRoute>
            }
          />
          <Route
            path="/docs/api"
            element={
              <AdminRoute>
                <ApiDocs />
              </AdminRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  </React.StrictMode>,
);
